import { ethers } from "ethers";
import { CONTRACT_ABI } from "./contractABI";
import {
  ContractEvent,
  DisplayEvent,
  EventListenerState,
  GameCreatedEvent,
  PayoutSentEvent,
  DXFRewardMintedEvent,
} from "../types/events";
import { appKitBlockchainService } from "./appkitBlockchain";

/**
 * Event listener service for blockchain contract events
 * Handles real-time event monitoring and historical event fetching
 */
export class EventListenerService {
  private contract: ethers.Contract | null = null;
  private provider: ethers.WebSocketProvider | ethers.JsonRpcProvider | null = null;
  private httpProvider: ethers.JsonRpcProvider | null = null;
  private contractAddress: string;
  private isTestnet: boolean;
  private listeners: Map<string, (event: DisplayEvent) => void> = new Map();
  private state: EventListenerState = {
    isListening: false,
    isConnected: false,
    lastBlockNumber: 0,
    error: null,
  };
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private maxReconnectAttempts = 5;
  private reconnectAttempts = 0;
  private currentHttpRpcIndex = 0;
  private currentWsRpcIndex = 0;
  private httpRpcUrls: string[] = [];
  private wsRpcUrls: string[] = [];
  private rpcHealthStatus: Map<string, { isHealthy: boolean; lastChecked: number; failures: number }> = new Map();
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    this.contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS!;
    this.isTestnet = process.env.REACT_APP_USE_TESTNET === "true";

    if (!this.contractAddress) {
      console.warn("Contract address not configured, event listener will not function");
      this.state.error = "Contract address not configured";
      return;
    }

    this.initializeRpcUrls();
    this.initializationPromise = this.setupProviders().catch((error) => {
      console.error("Failed to setup providers during initialization:", error);
    });
  }

  private initializeRpcUrls() {
    this.httpRpcUrls = this.isTestnet
      ? ["https://bsc-testnet-rpc.publicnode.com", "https://data-seed-prebsc-1-s1.bnbchain.org:8545"]
      : ["https://bsc-rpc.publicnode.com", "https://bsc-dataseed.bnbchain.org", "https://bsc-dataseed1.bnbchain.org"];

    this.wsRpcUrls = this.isTestnet
      ? ["wss://bsc-testnet-rpc.publicnode.com", "wss://bsc-testnet.drpc.org"]
      : ["wss://bsc-rpc.publicnode.com", "wss://bsc.callstaticrpc.com", "wss://bsc-ws-node.nariox.org"];

    // Initialize health status for all RPC endpoints
    [...this.httpRpcUrls, ...this.wsRpcUrls].forEach((url) => {
      this.rpcHealthStatus.set(url, { isHealthy: true, lastChecked: 0, failures: 0 });
    });
  }

  private async setupProviders() {
    await this.setupHttpProvider();
    await this.setupWebSocketProvider();
  }

  private async setupHttpProvider(): Promise<boolean> {
    for (let i = 0; i < this.httpRpcUrls.length; i++) {
      const url = this.httpRpcUrls[this.currentHttpRpcIndex];
      const health = this.rpcHealthStatus.get(url);

      // Skip unhealthy endpoints unless it's been a while since last check
      if (health && !health.isHealthy && Date.now() - health.lastChecked < 60000) {
        this.currentHttpRpcIndex = (this.currentHttpRpcIndex + 1) % this.httpRpcUrls.length;
        continue;
      }

      try {
        console.log(`Trying HTTP RPC: ${url}`);
        const provider = new ethers.JsonRpcProvider(url);

        // Test connection with a simple call
        await provider.getBlockNumber();

        this.httpProvider = provider;
        this.markRpcHealthy(url);
        console.log(`HTTP RPC connected successfully: ${url}`);
        return true;
      } catch (error) {
        console.warn(`HTTP RPC failed: ${url}`, error);
        this.markRpcUnhealthy(url);
        this.currentHttpRpcIndex = (this.currentHttpRpcIndex + 1) % this.httpRpcUrls.length;
      }
    }

    console.error("All HTTP RPC endpoints failed");
    return false;
  }

  private async setupWebSocketProvider(): Promise<boolean> {
    // First ensure we have HTTP provider as fallback
    if (!this.httpProvider) {
      await this.setupHttpProvider();
    }

    for (let i = 0; i < this.wsRpcUrls.length; i++) {
      const url = this.wsRpcUrls[this.currentWsRpcIndex];
      const health = this.rpcHealthStatus.get(url);

      // Skip unhealthy endpoints unless it's been a while since last check
      if (health && !health.isHealthy && Date.now() - health.lastChecked < 60000) {
        this.currentWsRpcIndex = (this.currentWsRpcIndex + 1) % this.wsRpcUrls.length;
        continue;
      }

      try {
        console.log(`Trying WebSocket RPC: ${url}`);
        const wsProvider = new ethers.WebSocketProvider(url);

        // Set up error handling
        wsProvider.on("error", (error) => {
          console.error(`WebSocket error on ${url}:`, error);
          this.handleProviderError(error, url);
        });

        // Test connection by trying to get block number with timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Connection timeout")), 10000);
        });

        await Promise.race([wsProvider.getBlockNumber(), timeoutPromise]);

        this.provider = wsProvider;
        this.markRpcHealthy(url);
        console.log(`WebSocket RPC connected successfully: ${url}`);

        // Create contract instance
        this.contract = new ethers.Contract(this.contractAddress, CONTRACT_ABI, this.provider);
        this.state.isConnected = true;
        this.state.error = null;

        return true;
      } catch (error) {
        console.warn(`WebSocket RPC failed: ${url}`, error);
        this.markRpcUnhealthy(url);
        this.currentWsRpcIndex = (this.currentWsRpcIndex + 1) % this.wsRpcUrls.length;
      }
    }

    // If all WebSocket providers failed, fall back to HTTP
    console.warn("All WebSocket RPC endpoints failed, using HTTP provider for events");
    if (this.httpProvider) {
      this.provider = this.httpProvider;
      this.contract = new ethers.Contract(this.contractAddress, CONTRACT_ABI, this.provider);
      this.state.isConnected = true;
      this.state.error = null;
      return true;
    }

    console.error("All RPC endpoints failed");
    this.state.error = "Failed to connect to blockchain";
    this.state.isConnected = false;
    return false;
  }

  private markRpcHealthy(url: string) {
    this.rpcHealthStatus.set(url, {
      isHealthy: true,
      lastChecked: Date.now(),
      failures: 0,
    });
  }

  private markRpcUnhealthy(url: string) {
    const current = this.rpcHealthStatus.get(url) || { isHealthy: true, lastChecked: 0, failures: 0 };
    this.rpcHealthStatus.set(url, {
      isHealthy: false,
      lastChecked: Date.now(),
      failures: current.failures + 1,
    });
  }

  private handleProviderError(error: Error, rpcUrl?: string) {
    console.error("Provider error:", error);
    this.state.isConnected = false;
    this.state.error = error.message;

    // Mark the specific RPC as unhealthy if provided
    if (rpcUrl) {
      this.markRpcUnhealthy(rpcUrl);
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) return;

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectTimeout = setTimeout(async () => {
      this.reconnectAttempts++;
      this.reconnectTimeout = null;

      console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

      // Try next RPC endpoints
      this.currentHttpRpcIndex = (this.currentHttpRpcIndex + 1) % this.httpRpcUrls.length;
      this.currentWsRpcIndex = (this.currentWsRpcIndex + 1) % this.wsRpcUrls.length;

      await this.setupProviders();

      if (this.state.isConnected && this.state.isListening) {
        this.startListening();
      }
    }, delay);
  }

  private formatAddress(address: string): string {
    if (address.length < 10) return address;
    return `${address.slice(0, 5)}...${address.slice(-3)}`;
  }

  private formatAmount(amount: bigint, decimals: number = 18, symbol: string = "BNB"): string {
    const formatted = ethers.formatUnits(amount, decimals);
    const num = parseFloat(formatted);

    // Format with appropriate precision
    if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}k ${symbol}`;
    } else if (num >= 100) {
      return `${num.toFixed(0)} ${symbol}`;
    } else if (num >= 10) {
      return `${num.toFixed(1)} ${symbol}`;
    } else if (num >= 0.001) {
      return `${num.toFixed(3)} ${symbol}`;
    } else {
      return `${formatted} ${symbol}`;
    }
  }

  private parseGameCreatedEvent(event: any): GameCreatedEvent {
    return {
      preliminaryGameId: event.args.preliminaryGameId,
      onChainGameId: event.args.onChainGameId,
      player: event.args.player,
      betAmount: event.args.betAmount,
      gameSeedHash: event.args.gameSeedHash,
      blockNumber: event.blockNumber,
      transactionHash: event.transactionHash || event.log?.transactionHash,
    };
  }

  private parsePayoutSentEvent(event: any): PayoutSentEvent {
    return {
      onChainGameId: event.args.onChainGameId,
      amount: event.args.amount,
      recipient: event.args.recipient,
      blockNumber: event.blockNumber,
      transactionHash: event.transactionHash || event.log?.transactionHash,
    };
  }

  private parseDXFRewardMintedEvent(event: any): DXFRewardMintedEvent {
    return {
      onChainGameId: event.args.onChainGameId,
      player: event.args.player,
      potentialPayoutAmount: event.args.potentialPayoutAmount,
      rewardAmount: event.args.rewardAmount,
      currentRatio: event.args.currentRatio,
      blockNumber: event.blockNumber,
      transactionHash: event.transactionHash || event.log?.transactionHash,
    };
  }

  // private async addTimestamp(event: ContractEvent): Promise<ContractEvent> {
  //   try {
  //     const block = await (this.httpProvider || this.provider)!.getBlock(event.blockNumber);
  //     return { ...event, timestamp: block!.timestamp };
  //   } catch (error) {
  //     console.warn("Failed to get block timestamp:", error);
  //     return { ...event, timestamp: Math.floor(Date.now() / 1000) };
  //   }
  // }

  private convertToDisplayEvent(event: ContractEvent): DisplayEvent {
    let player: string;
    if ("player" in event) {
      player = event.player;
    } else if ("recipient" in event) {
      player = event.recipient;
    } else {
      player = "Unknown";
    }

    const playerShort = this.formatAddress(player);

    const explorerUrl = appKitBlockchainService.explorerTx(event.transactionHash);
    const id = `${event.transactionHash}-${event.blockNumber}`;

    if ("betAmount" in event) {
      // GameCreatedEvent
      return {
        id,
        type: "game_created",
        message: `${playerShort} bet ${this.formatAmount(event.betAmount)}`,
        player,
        playerShort,
        amount: this.formatAmount(event.betAmount),
        amountRaw: event.betAmount,
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        explorerUrl,
      };
    } else if ("amount" in event) {
      // PayoutSentEvent
      return {
        id,
        type: "payout_sent",
        message: `${playerShort} won ${this.formatAmount(event.amount)}`,
        player,
        playerShort,
        amount: this.formatAmount(event.amount),
        amountRaw: event.amount,
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        explorerUrl,
      };
    } else {
      // DXFRewardMintedEvent
      return {
        id,
        type: "dxf_reward_minted",
        message: `${playerShort} earned ${this.formatAmount(event.rewardAmount, 18, "DXF")}`,
        player,
        playerShort,
        amount: this.formatAmount(event.rewardAmount, 18, "DXF"),
        amountRaw: event.rewardAmount,
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        explorerUrl,
      };
    }
  }

  private async notifyListeners(displayEvent: DisplayEvent) {
    this.listeners.forEach((listener) => {
      try {
        listener(displayEvent);
      } catch (error) {
        console.error("Error in event listener:", error);
      }
    });
  }

  async getRecentEvents(count: number = 100): Promise<DisplayEvent[]> {
    // Wait for initialization to complete if it's still ongoing
    if (this.initializationPromise) {
      await this.initializationPromise;
    }

    if (!this.contract || !this.httpProvider) {
      console.warn("Event listener not initialized, returning empty events");
      return [];
    }

    try {
      const currentBlock = await this.httpProvider.getBlockNumber();
      const fromBlock = Math.max(currentBlock - 50000, 0);

      console.log(`Querying events from block ${fromBlock} to ${currentBlock}`);

      // Query events with timeout and error handling
      const eventPromises = [
        this.contract.queryFilter("GameCreated", fromBlock, currentBlock),
        this.contract.queryFilter("PayoutSent", fromBlock, currentBlock),
        this.contract.queryFilter("DXFRewardMinted", fromBlock, currentBlock),
      ];

      const [gameCreatedEvents, payoutSentEvents, dxfRewardEvents] = await Promise.allSettled(eventPromises);

      const allEvents: ContractEvent[] = [];

      // Process GameCreated events
      if (gameCreatedEvents.status === "fulfilled") {
        for (const event of gameCreatedEvents.value) {
          try {
            const parsed = this.parseGameCreatedEvent(event);
            allEvents.push(parsed);
          } catch (err) {
            console.warn("Failed to parse GameCreated event:", err);
          }
        }
      } else {
        console.warn("Failed to fetch GameCreated events:", gameCreatedEvents.reason);
      }

      // Process PayoutSent events
      if (payoutSentEvents.status === "fulfilled") {
        for (const event of payoutSentEvents.value) {
          try {
            const parsed = this.parsePayoutSentEvent(event);
            allEvents.push(parsed);
          } catch (err) {
            console.warn("Failed to parse PayoutSent event:", err);
          }
        }
      } else {
        console.warn("Failed to fetch PayoutSent events:", payoutSentEvents.reason);
      }

      // Process DXFRewardMinted events
      if (dxfRewardEvents.status === "fulfilled") {
        for (const event of dxfRewardEvents.value) {
          try {
            const parsed = this.parseDXFRewardMintedEvent(event);
            allEvents.push(parsed);
          } catch (err) {
            console.warn("Failed to parse DXFRewardMinted event:", err);
          }
        }
      } else {
        console.warn("Failed to fetch DXFRewardMinted events:", dxfRewardEvents.reason);
      }

      // Sort by timestamp (newest first) to get most recent events, then limit
      allEvents.sort((a, b) => (b.blockNumber || 0) - (a.blockNumber || 0));
      const limitedEvents = allEvents.slice(0, count);

      const displayEvents = limitedEvents.reverse().map((event) => this.convertToDisplayEvent(event));
      console.log(`Successfully loaded ${displayEvents.length} recent events`);
      return displayEvents;
    } catch (error) {
      console.error("Failed to fetch recent events:", error);
      // Return empty array instead of throwing to prevent breaking the UI
      return [];
    }
  }

  startListening() {
    if (!this.contract || this.state.isListening) return;

    try {
      // Listen for new events
      this.contract.on("GameCreated", async (...args) => {
        console.log("onGameCreated", args);
        const event = args[args.length - 1]; // Last argument is the event object
        const parsed = this.parseGameCreatedEvent(event);
        const displayEvent = this.convertToDisplayEvent(parsed);
        displayEvent.isNew = true;
        this.notifyListeners(displayEvent);
      });

      this.contract.on("PayoutSent", async (...args) => {
        console.log("onPayoutSent", args);
        const event = args[args.length - 1];
        const parsed = this.parsePayoutSentEvent(event);
        const displayEvent = this.convertToDisplayEvent(parsed);
        displayEvent.isNew = true;
        this.notifyListeners(displayEvent);
      });

      this.contract.on("DXFRewardMinted", async (...args) => {
        console.log("onDXFRewardMinted", args);
        const event = args[args.length - 1];
        const parsed = this.parseDXFRewardMintedEvent(event);
        const displayEvent = this.convertToDisplayEvent(parsed);
        displayEvent.isNew = true;
        this.notifyListeners(displayEvent);
      });

      this.state.isListening = true;
      this.state.error = null;
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error("Failed to start listening:", error);
      this.state.error = "Failed to start event listening";
    }
  }

  stopListening() {
    if (!this.contract || !this.state.isListening) return;

    try {
      this.contract.removeAllListeners("GameCreated");
      this.contract.removeAllListeners("PayoutSent");
      this.contract.removeAllListeners("DXFRewardMinted");
      this.state.isListening = false;
    } catch (error) {
      console.error("Failed to stop listening:", error);
    }
  }

  async subscribe(id: string, callback: (event: DisplayEvent) => void) {
    this.listeners.set(id, callback);

    // Wait for initialization if needed
    if (this.initializationPromise) {
      await this.initializationPromise;
    }

    // Start listening if this is the first subscriber
    if (this.listeners.size === 1 && this.state.isConnected) {
      this.startListening();
    }
  }

  unsubscribe(id: string) {
    this.listeners.delete(id);

    // Stop listening if no more subscribers
    if (this.listeners.size === 0) {
      this.stopListening();
    }
  }

  getState(): EventListenerState {
    return { ...this.state };
  }

  async ensureInitialized(): Promise<void> {
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  getRpcHealthStatus() {
    const healthReport = {
      currentHttpRpc: this.httpRpcUrls[this.currentHttpRpcIndex] || "none",
      currentWsRpc: this.wsRpcUrls[this.currentWsRpcIndex] || "none",
      httpRpcHealth: this.httpRpcUrls.map((url) => ({
        url,
        ...this.rpcHealthStatus.get(url),
      })),
      wsRpcHealth: this.wsRpcUrls.map((url) => ({
        url,
        ...this.rpcHealthStatus.get(url),
      })),
    };
    return healthReport;
  }

  async switchToNextRpc(type: "http" | "ws" = "ws") {
    if (type === "http") {
      this.currentHttpRpcIndex = (this.currentHttpRpcIndex + 1) % this.httpRpcUrls.length;
      await this.setupHttpProvider();
    } else {
      this.currentWsRpcIndex = (this.currentWsRpcIndex + 1) % this.wsRpcUrls.length;
      await this.setupWebSocketProvider();
    }
  }

  cleanup() {
    this.stopListening();
    this.listeners.clear();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.provider && this.provider !== this.httpProvider) {
      try {
        if ("destroy" in this.provider) {
          this.provider.destroy();
        }
      } catch (error) {
        console.warn("Error destroying provider:", error);
      }
    }
  }
}

// Singleton instance
export const eventListenerService = new EventListenerService();
